import axios from "axios"

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:5000"

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true, // Include credentials (cookies) with requests
  timeout: 10000, // 10 second timeout
})

// Global token expiration handler
const handleTokenExpiration = () => {
  console.log("Token expired, triggering auto logout...")

  // Clear local storage
  localStorage.removeItem("token")
  localStorage.removeItem("user")

  // Dispatch custom events for the global system
  window.dispatchEvent(new CustomEvent("tokenExpired"))
  window.dispatchEvent(new CustomEvent("userLoggedOut"))

  // Redirect to login if not already there
  if (window.location.pathname !== "/") {
    window.location.href = "/"
  }
}

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token")
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  },
)

// Response interceptor to handle token expiration
api.interceptors.response.use(
  (response) => {
    return response
  },
  (error) => {
    const status = error?.response?.status
    const url = error?.config?.url
    const method = error?.config?.method
    const data = error?.response?.data
    console.error("API Error:", { status, method, url, data, error })

    // Errors are normalised to a plain Error so callers can rely on .message,
    // but the HTTP status is carried along with it. Without that, a caller
    // cannot tell "the server refused this" from "the server was unreachable" —
    // which matters for anything deciding whether a request is worth retrying.
    const rejectWith = (message, httpStatus) => {
      const normalised = new Error(message)
      if (httpStatus != null) {
        normalised.status = httpStatus
        normalised.response = error.response
      }
      return Promise.reject(normalised)
    }

    if (error.response) {
      const { status, data } = error.response

      // Check for token expiration
      if (status === 401 || (data && data.message && data.message.includes("token"))) {
        console.log("Token expired, triggering auto logout...")
        handleTokenExpiration()
        return rejectWith("Session expired. Please log in again.", status)
      }

      // Handle other HTTP errors
      const errorMessage =
        data?.message ||
        data?.error ||
        data?.details ||
        `HTTP ${status}: ${error.response.statusText}`
      return rejectWith(errorMessage, status)
    } else if (error.request) {
      // Network error — no status, because the request never got an answer.
      return rejectWith("Network error - please check your connection", null)
    } else {
      // Other errors
      return rejectWith(error.message || "An unexpected error occurred", null)
    }
  },
)

export default api
